import { Controller } from "@hotwired/stimulus"

// Live progress for a bulk run: every item listed up front, each row moving from
// waiting to processing to its outcome, with tiles counting what is completed,
// naming what is processing now, and counting what is still waiting.
//
// Knows nothing about what is being processed. The controller running the job
// (invoice-bulk) drives it through an outlet:
//
//   progress.start([{ key: "abc", label: "#INV-001" }, …])
//   progress.processing("abc", "Filing with FBR…")
//   progress.complete("abc", "success", "Filed · IRN …")   // or "warning" / "failure"
//   progress.finish()                                       // unstarted rows → not attempted
const STATES = {
  waiting:    { icon: "schedule",          color: "text-foreground-subtle" },
  processing: { icon: "progress_activity", color: "text-primary", spin: true },
  success:    { icon: "check_circle",      color: "text-success" },
  warning:    { icon: "warning",           color: "text-warning" },
  failure:    { icon: "error",             color: "text-destructive" },
  skipped:    { icon: "remove_circle",     color: "text-foreground-subtle" }
}
const STATE_COLORS = Object.values(STATES).map((state) => state.color)

export default class extends Controller {
  static targets = ["completed", "failed", "processing", "waiting", "bar", "list", "rowTemplate"]

  connect() {
    this.rows = new Map()
  }

  // Lists every item as waiting and shows the panel.
  start(items) {
    this.rows = new Map()
    this.listTarget.replaceChildren()

    items.forEach(({ key, label }) => {
      const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
      row.querySelector("[data-label]").textContent = label
      this.listTarget.append(row)
      this.rows.set(key, row)
      this.setRow(row, "waiting", "Waiting")
    })

    this.element.hidden = false
    this.render()
  }

  processing(key, message) {
    const row = this.rows.get(key)
    this.setRow(row, "processing", message)
    row.scrollIntoView({ block: "nearest" })
    this.render()
  }

  complete(key, state, message) {
    this.setRow(this.rows.get(key), state, message)
    this.render()
  }

  // Ends the run. Rows never started — after a Stop or a halt — were not attempted.
  finish() {
    this.rows.forEach((row) => {
      if (row.dataset.state === "waiting") this.setRow(row, "skipped", "Not attempted")
    })
    this.render()
  }

  hide() {
    this.element.hidden = true
  }

  setRow(row, state, message) {
    const style = STATES[state] || STATES.failure
    const icon  = row.querySelector("[data-icon]")

    row.dataset.state = state
    icon.textContent  = style.icon
    icon.classList.remove(...STATE_COLORS)
    icon.classList.add(style.color)
    icon.classList.toggle("animate-spin", Boolean(style.spin))
    row.querySelector("[data-message]").textContent = message
  }

  render() {
    const rows       = [ ...this.rows.values() ]
    const inState    = (...states) => rows.filter((row) => states.includes(row.dataset.state))
    const completed  = inState("success", "warning", "failure").length
    const failed     = inState("warning", "failure").length
    const processing = inState("processing")

    this.completedTarget.textContent  = `${completed} / ${rows.length}`
    this.failedTarget.textContent     = `${failed} failed`
    this.failedTarget.hidden          = failed === 0
    this.processingTarget.textContent = processing.length
      ? processing.map((row) => row.querySelector("[data-label]").textContent).join(", ")
      : "—"
    this.waitingTarget.textContent    = inState("waiting").length
    this.barTarget.style.width        = `${rows.length ? Math.round((completed / rows.length) * 100) : 0}%`
  }
}
